/* ══════════════════════════════════════════════════════════════════
   🛒 SHARED CART DRAWER REFRESH — single source of truth
   ══════════════════════════════════════════════════════════════════
   Loaded ONCE, globally, BEFORE sleep-setup-widget.js, the Sleep
   Essentials section, and free-gift.js — all three should call
   window.swRefreshCartDrawer() after their own cart mutations instead
   of each rolling their own refresh logic.

   WHY THIS EXISTS:
   Each of those three scripts previously had its own way of asking
   the cart drawer to re-render (CartAddEvent dispatch in two of them,
   a direct morphSection call in free-gift.js). When two mutations
   happened close together — e.g. adding a protector, which then
   triggers free-gift.js to add its matching gift a moment later —
   TWO independent refresh calls fired near-simultaneously, racing
   each other for the same DOM. That race is what produced the
   symptoms reported: a blank/white gap, or the whole drawer visibly
   closing and reopening.

   THE FIX: one shared, debounced entry point. Multiple calls within
   a short window (default 120ms) collapse into a single morph pass.
   Internally this always uses morphSection(sectionId, html,
   { mode: 'hydration' }) — the exact call the theme's own native
   quantity +/- flow uses, which is the one mechanism proven not to
   disturb the drawer's live open/closed state (see the long comment
   in free-gift.js's earlier version for the full diagnosis of why
   other approaches — CartAddEvent, or morph without hydration mode —
   caused a close+reopen flash).
   ══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (window.__swCartDrawerRefreshInit) return;
  window.__swCartDrawerRefreshInit = true;

  console.log('🛒 Shared cart drawer refresh utility — active');

  var debounceTimer = null;
  var inFlight = false;
  var queued = false;
  var CART_ITEMS_SCROLL_SELECTOR = '.cart-drawer__items';

  function getCartItemsSectionId() {
    var el = document.querySelector('cart-items-component');
    return el && el.dataset && el.dataset.sectionId;
  }

  /* If nothing has opened the drawer yet (e.g. this is the very first
     add-to-cart on the page and no CartAddEvent-driven auto-open has
     run), open it directly via the native component's own .open()
     method — this never touches content, so it can't cause any morph
     race; it just makes sure the drawer becomes visible at all. */
  function openDrawerIfClosed() {
    var drawerComponent = document.querySelector('cart-drawer-component');
    var dialog = drawerComponent && drawerComponent.querySelector
      ? drawerComponent.querySelector('dialog')
      : document.querySelector('cart-drawer-component dialog');
    if (dialog && !dialog.open && drawerComponent && typeof drawerComponent.open === 'function') {
      try { drawerComponent.open(); } catch (e) { /* ignore */ }
    }
  }

  var CART_SUMMARY_SELECTOR = '.cart-drawer__summary';
  var SHIMMER_CLASS = 'sw-summary-loading';

  function setSummaryLoading(isLoading) {
    var summaryEl = document.querySelector(CART_SUMMARY_SELECTOR);
    if (summaryEl) summaryEl.classList.toggle(SHIMMER_CLASS, isLoading);
  }

  var COOLDOWN_MS = 650;
  var cooldownTimer = null;

  function doRefresh() {
    if (inFlight) { queued = true; return; }
    // 🩹 FIX: a call arriving during the post-morph cooldown window is
    // very likely the SAME user action still settling (e.g.
    // free-gift.js's gift-sync finishing a beat after the item itself
    // was added) — cancel the pending "hide shimmer" and fold straight
    // into another refresh cycle instead of ever revealing content
    // that's about to change again.
    if (cooldownTimer) {
      clearTimeout(cooldownTimer);
      cooldownTimer = null;
    }
    inFlight = true;
    window.__swDrawerMorphInFlight = true;
    // 🩹 FIX: .cart-drawer__summary (Estimated total + "This is a
    // gift" + Checkout button) lives INSIDE the same
    // cart-items-component section this morph replaces — there's no
    // way to morph just the items list without it. Rather than let
    // that block visibly flicker/jump mid-morph, show a shimmer over
    // it for the (usually well under a second) duration of the
    // refresh, then reveal the freshly-morphed, correct content in
    // one clean step.
    setSummaryLoading(true);

    var sectionId = getCartItemsSectionId();
    var morphSectionFn = window.__swMorphSection;

    if (!sectionId || !morphSectionFn) {
      console.warn('🛒 swRefreshCartDrawer: missing section id or morphSection capture — check the module-script in theme.liquid loaded correctly.');
      finish();
      return;
    }

    // 🩹 FIX: preserve scroll position across the morph. Even
    // hydration-mode morphSection can reset scrollTop on the
    // scrollable items container when it patches its children, which
    // is why the drawer visibly jumped back to the top on every
    // add/remove. Capture it right before the morph and restore it
    // (twice, via rAF, since layout may not be final on the very next
    // frame) right after.
    var scrollEl = document.querySelector(CART_ITEMS_SCROLL_SELECTOR);
    var savedScrollTop = scrollEl ? scrollEl.scrollTop : null;

    function restoreScroll() {
      if (savedScrollTop == null) return;
      var el = document.querySelector(CART_ITEMS_SCROLL_SELECTOR);
      if (el) el.scrollTop = savedScrollTop;
      requestAnimationFrame(function () {
        var el2 = document.querySelector(CART_ITEMS_SCROLL_SELECTOR);
        if (el2) el2.scrollTop = savedScrollTop;
      });
    }

  fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-SW-Silent-Cart-Sync': '1'
      },
      body: JSON.stringify({ sections: sectionId, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data.sections && data.sections[sectionId];
        if (html) {
          try {
            morphSectionFn(sectionId, html, { mode: 'hydration' });
            restoreScroll();
          } catch (e) {
            console.error('🛒 morphSection() call failed:', e);
          }
        }
        // 🩹 REMOVED: this used to dispatch 'sleep-setup:cart-changed'
        // here — but every refresh dispatching that event meant every
        // listener (free-gift.js's own sync, Sleep Setup's price-sync)
        // could react by triggering ANOTHER refresh, which dispatched
        // the event again, and so on — a feedback loop that caused
        // continuous cart flicker/jitter and repeated price
        // recalculation while scrolling/idle. Sleep Setup's grid
        // staying blank after a morph (the original reason this was
        // added) is already handled more safely by the direct
        // grid-observer inside sleep-setup-widget.js's
        // connectedCallback, which reacts to the grid's own DOM state
        // rather than firing a global event on every single refresh.
      })
      .catch(function (e) {
        console.error('🛒 Cart drawer refresh failed:', e);
      })
      .then(finish, finish);

    function finish() {
      inFlight = false;
      window.__swDrawerMorphInFlight = false;
      if (queued) {
        queued = false;
        setTimeout(doRefresh, 50);
      } else {
        clearTimeout(cooldownTimer);
        cooldownTimer = setTimeout(function () {
          cooldownTimer = null;
          setSummaryLoading(false);
        }, COOLDOWN_MS);
      }
    }
  }

  /* ── Public API ──
     window.swRefreshCartDrawer(options)
       options.delay        — debounce window in ms (default 120)
       options.openIfClosed — also call the drawer's own .open() if
                               it's currently closed (default false;
                               pass true from an "add" flow so the
                               very first add on a page still opens
                               the drawer, since a content-only morph
                               can't open a closed dialog by itself)
  */
  window.swRefreshCartDrawer = function (options) {
    options = options || {};
    if (options.openIfClosed) openDrawerIfClosed();
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doRefresh, options.delay == null ? 120 : options.delay);
  };
})();
